// libraryQuery — the library's query LANGUAGE (library search 2026-10-01,
// promoted from to-implement/library-search/searchSyntax.ts): the query TEXT is
// the single source of truth. The kind tabs, the filter chips and the
// autocomplete all read and write it, and the live text rides in the URL.
//
// Grammar (Slack / GitHub style). Space = AND.
//   word        a text term: word-prefix match, one typo allowed at 4+ letters
//   "a phrase"  an exact (folded) phrase
//   key:value   a filter; `-key:value` excludes. Comma = OR inside one value.
// The query syntax is English whatever the UI language (labels around the
// field are localized, the syntax is not):
//   type:     is:        source / material / package / file
//   author:              prefix of any author's name
//   created:  during:    `2026` or `2026-09` (time is a filter, not the
//                        order — the library is a register)
//   status:              archived (the Archived tab)
// `by:` is deliberately NOT an alias: it is History's actor qualifier
// (query.ts). The parse is TOTAL: an unknown or unfinished modifier is text,
// and `hintOf` says so (a gentle note listing the valid modifiers), so a
// misspelt filter never fails silently.
//
// Pure: no I/O, no clock. Matching folds case and diacritics EXCEPT å/ä/ö (the
// rule the server's content search applies), per character so match indices
// line up with the original text for highlighting.

import { citedName } from "./authorName.js";
import type { SearchResult, SearchResultKind } from "./searchModel.js";

// === Modifiers ===============================================================

export type ModifierKey = "type" | "author" | "created" | "status";

type ModifierSpec = {
    readonly key: ModifierKey;
    readonly aliases: readonly string[];
};

const MODIFIERS: readonly ModifierSpec[] = [
    { key: "type", aliases: ["is"] },
    { key: "author", aliases: [] },
    { key: "created", aliases: ["during"] },
    { key: "status", aliases: [] },
];

const namesOf = (spec: ModifierSpec): readonly string[] => [
    spec.key,
    ...spec.aliases,
];

// === Folding + matching ======================================================

const foldChar = (c: string): string => {
    const lower = c.toLowerCase();
    if (lower === "å" || lower === "ä" || lower === "ö") return lower;
    const base = lower.normalize("NFD").charAt(0);
    return base === "" ? lower : base;
};
const fold = (s: string): string => Array.from(s).map(foldChar).join("");

// Optimal-string-alignment distance (Damerau: a swap of neighbours costs 1).
const osa = (a: string, b: string): number => {
    const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
        Array.from({ length: b.length + 1 }, (_, j) =>
            i === 0 ? j : j === 0 ? i : 0,
        ),
    );
    for (let i = 1; i <= a.length; i += 1) {
        for (let j = 1; j <= b.length; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            const best = Math.min(
                d[i - 1][j] + 1,
                d[i][j - 1] + 1,
                d[i - 1][j - 1] + cost,
            );
            d[i][j] =
                i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]
                    ? Math.min(best, d[i - 2][j - 2] + 1)
                    : best;
        }
    }
    return d[a.length][b.length];
};

// One matched span of a text, half-open [start, end). `typo` = it needed the
// one-edit tolerance (the list then says what it showed instead).
export type WordMatch = {
    readonly start: number;
    readonly end: number;
    readonly typo: boolean;
};

const TYPO_MIN = 4;

// Every word of `text` the (folded) term matches: as a prefix, or — for terms
// of 4+ letters — within one edit of the word's same-length prefix.
const wordMatches = (text: string, term: string): readonly WordMatch[] =>
    // A term with punctuation inside ("v3.1", "ISO-9001") spans words: it
    // matches as written.
    /[^\p{L}\p{N}]/u.test(term)
        ? phraseMatches(text, term)
        : Array.from(fold(text).matchAll(/[\p{L}\p{N}]+/gu)).flatMap(
              (m): readonly WordMatch[] => {
                  const word = m[0];
                  const start = m.index ?? 0;
                  if (word.startsWith(term))
                      return [{ start, end: start + term.length, typo: false }];
                  return term.length >= TYPO_MIN &&
                      osa(word.slice(0, term.length), term) <= 1
                      ? [{ start, end: start + word.length, typo: true }]
                      : [];
              },
          );

const phraseMatches = (text: string, phrase: string): readonly WordMatch[] => {
    const at = fold(text).indexOf(phrase);
    return at < 0 ? [] : [{ start: at, end: at + phrase.length, typo: false }];
};

// === Terms ===================================================================

type TextTerm = { readonly type: "text"; readonly value: string };
type PhraseTerm = { readonly type: "phrase"; readonly value: string };
type FilterTerm = {
    readonly type: "filter";
    readonly key: ModifierKey;
    readonly values: readonly string[];
};
type ExclusionTerm = {
    readonly type: "exclusion";
    readonly key: ModifierKey;
    readonly values: readonly string[];
};
export type Term = TextTerm | PhraseTerm | FilterTerm | ExclusionTerm;
export type FreeTerm = TextTerm | PhraseTerm;

// A token keeps its raw text, so a chip or a tab removes exactly it.
export type Token = { readonly raw: string; readonly term: Term };

const unquote = (s: string): string => s.replace(/^"/, "").replace(/"$/, "");

const specOf = (name: string): ModifierSpec | undefined => {
    const f = fold(name);
    return MODIFIERS.find((m) => namesOf(m).some((n) => fold(n) === f));
};

const termOf = (raw: string): Term => {
    const modifier = /^(-?)([^\s:"]+):(.+)$/u.exec(raw);
    const spec = modifier === null ? undefined : specOf(modifier[2]);
    if (modifier !== null && spec !== undefined) {
        const values = unquote(modifier[3])
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v !== "");
        if (values.length > 0)
            return modifier[1] === "-"
                ? { type: "exclusion", key: spec.key, values }
                : { type: "filter", key: spec.key, values };
    }
    return freeTermOf(raw);
};

// One free word or "phrase" as a term (the content search's terms are
// checked against a row's title and authors with it).
export const freeTermOf = (raw: string): FreeTerm =>
    raw.startsWith('"')
        ? { type: "phrase", value: fold(unquote(raw)) }
        : { type: "text", value: fold(raw) };

// A modifier still waiting for its value (`author:`): no term yet.
const isUnfinishedModifier = (raw: string): boolean => {
    const m = /^-?([^\s:"]+):"?$/u.exec(raw);
    return m !== null && specOf(m[1] ?? "") !== undefined;
};

// A query's words: `key:"quoted value"`, a "phrase", or a bare word.
const WORDS = /-?[^\s:"]+:"[^"]*"?|"[^"]*"?|\S+/gu;

export const tokenize = (query: string): readonly Token[] =>
    Array.from(query.matchAll(WORDS))
        .filter((m) => !isUnfinishedModifier(m[0]))
        .map((m) => ({ raw: m[0], term: termOf(m[0]) }))
        .filter(
            (t) =>
                !(
                    (t.term.type === "text" || t.term.type === "phrase") &&
                    t.term.value === ""
                ),
        );

export const freeTermsOf = (tokens: readonly Token[]): readonly FreeTerm[] =>
    tokens.flatMap((t) =>
        t.term.type === "text" || t.term.type === "phrase" ? [t.term] : [],
    );

// The free words and "phrases" as typed (modifiers stripped): what the
// content search runs, one search per word.
export const freeWordsOf = (tokens: readonly Token[]): readonly string[] =>
    tokens
        .filter((t) => t.term.type === "text" || t.term.type === "phrase")
        .map((t) => t.raw);

export const removeToken = (query: string, raw: string): string =>
    tokenize(query)
        .map((t) => t.raw)
        .filter((r) => r !== raw)
        .join(" ");

// Every match of the free terms in `text` (empty when some term misses it).
export const matchesIn = (
    text: string,
    terms: readonly FreeTerm[],
): readonly WordMatch[] => {
    const per = terms.map((t) =>
        t.type === "phrase"
            ? phraseMatches(text, t.value)
            : wordMatches(text, t.value),
    );
    return per.every((m) => m.length > 0) ? per.flat() : [];
};

// Does ONE free term hit `text`? (Each term may hit a different field.)
export const termHits = (text: string, term: FreeTerm): boolean =>
    (term.type === "phrase"
        ? phraseMatches(text, term.value)
        : wordMatches(text, term.value)
    ).length > 0;

// === Values ==================================================================

const KIND_WORDS: readonly (readonly [SearchResultKind, readonly string[]])[] =
    [
        ["source", ["sources"]],
        ["material", ["materials"]],
        ["package", ["packages", "export"]],
        ["file", ["files"]],
    ];
const kindOfWord = (word: string): SearchResultKind | undefined =>
    KIND_WORDS.find(([kind, aliases]) =>
        [kind, ...aliases].some((w) => fold(w) === fold(word)),
    )?.[0];

const ARCHIVED_WORD = "archived";
const isArchivedWord = (word: string): boolean => fold(word) === ARCHIVED_WORD;

// The author names a filter matches: the full names, else the byline.
const authorNames = (r: SearchResult): readonly string[] =>
    r.authors ?? (r.byline === undefined ? [] : [r.byline]);

const holds = (r: SearchResult, key: ModifierKey, value: string): boolean => {
    const v = fold(value);
    switch (key) {
        case "type":
            return kindOfWord(value) === r.kind;
        case "author":
            // A prefix of any word run of a name: `natur`, `"natur & kultur"`.
            return authorNames(r).some((a) => ` ${fold(a)}`.includes(` ${v}`));
        case "created":
            return r.createdAt?.startsWith(value) === true;
        case "status":
            return isArchivedWord(value) && r.archived === true;
    }
};

// Keep the rows every filter holds for (comma = OR inside one filter) and no
// exclusion does. Archived rows show ONLY under a `status:archived` filter.
export const applyFilters = (
    tokens: readonly Token[],
    pool: readonly SearchResult[],
): readonly SearchResult[] => {
    const terms = tokens.map((t) => t.term);
    const wantsArchived = terms.some(
        (t) => t.type === "filter" && t.key === "status",
    );
    return pool.filter(
        (r) =>
            (r.archived === true) === wantsArchived &&
            terms.every((t) =>
                t.type === "filter"
                    ? t.values.some((v) => holds(r, t.key, v))
                    : t.type === "exclusion"
                      ? !t.values.some((v) => holds(r, t.key, v))
                      : true,
            ),
    );
};

// === Scope <-> query (the kind tabs read and write the text) =================

// The tabs: everything, one kind, or the archived rows.
export type QueryScope = "all" | SearchResultKind | "archived";

// The tab the text selects: `status:` ⇒ archived; exactly one `type:` kind ⇒
// that kind; no `type:` ⇒ all; anything else (several kinds) ⇒ no tab.
export const scopeOfQuery = (tokens: readonly Token[]): QueryScope | null => {
    const filters = tokens.flatMap((t) =>
        t.term.type === "filter" ? [t.term] : [],
    );
    if (filters.some((f) => f.key === "status")) return "archived";
    const kinds = filters
        .filter((f) => f.key === "type")
        .flatMap((f) => f.values.map(kindOfWord));
    if (kinds.length === 0) return "all";
    const [only] = kinds;
    return kinds.length === 1 && only !== undefined ? only : null;
};

// The text with its tab tokens (`type:` / `status:`, either sign) replaced by
// the given scope's token, written first.
export const withScope = (query: string, scope: QueryScope): string => {
    const rest = tokenize(query)
        .filter(
            (t) =>
                !(
                    (t.term.type === "filter" || t.term.type === "exclusion") &&
                    (t.term.key === "type" || t.term.key === "status")
                ),
        )
        .map((t) => t.raw);
    const head =
        scope === "all"
            ? []
            : scope === "archived"
              ? [`status:${ARCHIVED_WORD}`]
              : [`type:${scope}`];
    return [...head, ...rest].join(" ");
};

// === Autocomplete =============================================================

type ModifierSuggestion = {
    readonly type: "modifier";
    readonly key: ModifierKey;
    readonly negated: boolean;
};
type ValueSuggestion = {
    readonly type: "value";
    readonly key: ModifierKey;
    readonly value: string;
    readonly count: number;
    readonly negated: boolean;
};
export type Suggestion = ModifierSuggestion | ValueSuggestion;

const SUGGESTION_LIMIT = 6;
const collator = new Intl.Collator("sv", { sensitivity: "base" });

const counted = (
    values: readonly string[],
): readonly (readonly [string, number])[] =>
    [...new Set(values)]
        .map((v): readonly [string, number] => [
            v,
            values.filter((x) => x === v).length,
        ])
        .sort((a, b) => b[1] - a[1] || collator.compare(a[0], b[0]));

// The values a modifier can take in this pool, each with how many rows have
// it (recognition over recall: you see who is in the library).
const valuesOf = (
    pool: readonly SearchResult[],
    key: ModifierKey,
): readonly (readonly [string, number])[] => {
    const live = pool.filter((r) => r.archived !== true);
    switch (key) {
        case "type":
            return counted(live.map((r) => r.kind));
        case "author":
            return counted(live.flatMap(authorNames).map(citedName));
        case "created":
            return counted(
                live.flatMap((r) =>
                    r.createdAt === undefined ? [] : [r.createdAt.slice(0, 7)],
                ),
            );
        case "status":
            return [
                [ARCHIVED_WORD, pool.filter((r) => r.archived === true).length],
            ];
    }
};

const partialOf = (query: string): string =>
    /\s$/u.test(query) ? "" : (query.split(/\s+/u).at(-1) ?? "");

// What the field offers for the token being typed: modifiers matching a
// prefix (`au` -> author:), or, after `key:`, its values with counts.
export const suggest = (
    pool: readonly SearchResult[],
    query: string,
): readonly Suggestion[] => {
    const partial = partialOf(query);
    if (partial === "" || partial.startsWith('"')) return [];
    const negated = partial.startsWith("-");
    const bare = negated ? partial.slice(1) : partial;
    const colon = bare.indexOf(":");
    if (colon < 0) {
        const f = fold(bare);
        return MODIFIERS.filter((m) =>
            namesOf(m).some((n) => fold(n).startsWith(f)),
        ).map((m) => ({ type: "modifier", key: m.key, negated }));
    }
    const spec = specOf(bare.slice(0, colon));
    if (spec === undefined) return [];
    const typed = fold(unquote(bare.slice(colon + 1)));
    return valuesOf(pool, spec.key)
        .filter(([v]) => fold(v).startsWith(typed))
        .filter(([v]) => fold(v) !== typed)
        .slice(0, SUGGESTION_LIMIT)
        .map(([value, count]) => ({
            type: "value",
            key: spec.key,
            value,
            count,
            negated,
        }));
};

const quoteIfSpaced = (v: string): string => (/\s/u.test(v) ? `"${v}"` : v);

// The query with its partial last token replaced by the accepted suggestion.
export const accept = (query: string, s: Suggestion): string => {
    const head = query.slice(0, query.length - partialOf(query).length);
    const sign = s.negated ? "-" : "";
    return s.type === "modifier"
        ? `${head}${sign}${s.key}:`
        : `${head}${sign}${s.key}:${quoteIfSpaced(s.value)} `;
};

// === Hints ===================================================================
// The parse never refuses, so a filter that does nothing must SAY so: a word
// shaped like `key:` that is no modifier, a modifier left without a value
// (not the one being typed), or a `type:` / `status:` value nothing has. The
// field shows one gentle note; the query still runs as written.

type UnknownModifier = {
    readonly type: "unknown_modifier";
    readonly name: string;
};
type MissingValue = {
    readonly type: "missing_value";
    readonly key: ModifierKey;
};
type UnknownValue = {
    readonly type: "unknown_value";
    readonly key: "type" | "status";
    readonly value: string;
};
export type QueryHint = UnknownModifier | MissingValue | UnknownValue;

const hintOfWord = (raw: string, typing: boolean): QueryHint | undefined => {
    const m = /^-?(\p{L}+):(.*)$/u.exec(raw);
    if (m === null) return undefined;
    const [, name = "", rest = ""] = m;
    const spec = specOf(name);
    if (spec === undefined)
        return rest.startsWith("//")
            ? undefined
            : { type: "unknown_modifier", name };
    const values = unquote(rest)
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v !== "");
    if (typing) return undefined;
    if (values.length === 0) return { type: "missing_value", key: spec.key };
    const key = spec.key;
    if (key !== "type" && key !== "status") return undefined;
    const bad = values.find((v) =>
        key === "type" ? kindOfWord(v) === undefined : !isArchivedWord(v),
    );
    return bad === undefined
        ? undefined
        : { type: "unknown_value", key, value: bad };
};

// The first word in the query that filters nothing. The word still being
// typed (last, no trailing space) only warns when no value can mend it.
export const hintOf = (query: string): QueryHint | undefined => {
    const words = Array.from(query.matchAll(WORDS)).map((m) => m[0]);
    const typingLast = !/\s$/u.test(query);
    return words
        .map((w, i) => hintOfWord(w, typingLast && i === words.length - 1))
        .find((h) => h !== undefined);
};
